import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import { inventoryHref } from "../../api/InventoryAPI";
import { logApiError, getUserMessage } from "../../api/apiError";

import { Category, CategoryParameter, ParameterDataType } from "../../types/Category";
import { Unit } from "../../types/Unit";
import DynamicForm from "../DynamicForm";
import { SearchFilter } from "../../types/SearchFilter";
import GeoSearchSelector from "../geo/GeoSearchSelector";
import type { GeoSearchByIdDTO } from "../../types/SearchFilter";
import { getAllUnits } from "../../api/UnitAPI";
import InventoryDetailModal from "../inventory/InventoryDetailModal";
import ContactActions from "./ContactActions";
import GeoAreaPicker from "../geo/GeoAreaPicker";
import { chainToKeys, coverageSummary } from "../../utils/coverage";

const boxStyle: React.CSSProperties = {
    background: "var(--bg-primary)",
    border: "1px solid var(--border-color)",
    borderRadius: "var(--radius-lg)",
    padding: "1.5rem",
    marginBottom: "1.5rem",
    position: "relative",
    overflow: "visible",
    boxShadow: "var(--shadow-sm)",
};

const boxHeaderStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    fontWeight: 600,
    fontSize: "1rem",
    color: "var(--text-primary)",
    marginBottom: "1rem",
    paddingBottom: "0.75rem",
    borderBottom: "1px solid var(--border-color)",
};

import CategoryTreeNavigator from "../category/CategoryTreeNavigator";

interface SearchProps {
    token: string | null;
}

const SearchComponent: React.FC<SearchProps> = ({ token }) => {
    // Full category (leaf) selected via the tree navigator, with parameters.
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

    const [units, setUnits] = useState<Unit[]>([]);

    // --- inventory results ---
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const [geoSearch, setGeoSearch] = useState<GeoSearchByIdDTO | null>(null);
    const [showLocation, setShowLocation] = useState<boolean>(false);
    const [detailItem, setDetailItem] = useState<any | null>(null);
    const [showService, setShowService] = useState<boolean>(false);
    const [coverageChain, setCoverageChain] = useState<string[] | null>(null);
    // Fixed-location categories (rentals, real estate): search by where the
    // listed thing is, matching the listing's own location chain.
    const [showItemLocation, setShowItemLocation] = useState<boolean>(false);
    const [itemGeoSearch, setItemGeoSearch] = useState<GeoSearchByIdDTO | null>(null);

    const toggleService = (checked: boolean) => {
        setShowService(checked);
        if (!checked) setCoverageChain(null);
    };

    const toggleItemLocation = (checked: boolean) => {
        setShowItemLocation(checked);
        if (!checked) setItemGeoSearch(null);
    };

    // Toggling the location filter off clears any selection so a hidden filter
    // can't silently narrow the search.
    const toggleLocation = (checked: boolean) => {
        setShowLocation(checked);
        if (!checked) setGeoSearch(null);
    };

    useEffect(() => {
        // Load units even for signed-out users so numeric parameters show the
        // unit name (e.g. "kg") rather than falling back to the raw unit id.
        getAllUnits(token ?? "").then(setUnits).catch(() => setUnits([]));
    }, [token]);

    const authHeaders = useMemo(() => {
        if (!token) return {};
        return {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        };
    }, [token]);

    const handleFormSubmit = (searchFilter: SearchFilter) => {
        const isService = !!selectedCategory?.hasServiceArea;
        const isItem = !!selectedCategory?.hasItemLocation;
        const payload: SearchFilter = {
            ...searchFilter,
            // Service categories use coverage; fixed-location categories use
            // the listing's own location; neither uses the store filter.
            geoSearch: isService || isItem ? null : geoSearch,
            coverageChain: isService ? coverageChain : null,
            itemGeoSearch: isItem ? itemGeoSearch : null,
        };

        console.log("Submitted Search Filter:", payload);
        inventorySearch(payload);
    };

    const inventorySearch = async (searchFilter: SearchFilter) => {
        // No token guard: general search is public. Signed-out users get the
        // anonymous GENERAL_SEARCH grant, so the request is sent without an
        // Authorization header (authHeaders is {} when there is no token).
        setLoading(true);
        setError(null);

        try {
            const response = await axiosInstance.post("/inventories/search", searchFilter, {
                headers: authHeaders,
            });

            console.log("inventory search result:", response.data);
            setSearchResults(response.data ?? []);
        } catch (err: any) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    };

    // ✅ SAFE parameters array (prevents map crash)
    const params = selectedCategory?.parameters ?? [];

    return (
        <div className="search-main-container">
            <h2 style={{ marginBottom: "1.5rem" }}>Listing Search</h2>

            <div style={boxStyle}>
                <div style={boxHeaderStyle}>
                    Category
                </div>

                <CategoryTreeNavigator
                    token={token!}
                    selectedLeafId={selectedCategory?.id ?? null}
                    onSelectLeaf={(full) => {
                        setSelectedCategory(full);
                        setSearchResults([]);
                        setError(null);
                    }}
                />

                {selectedCategory && (
                    <div style={{ fontSize: "0.875rem", marginTop: "0.5rem" }}>
                        <span className="status-selected">Selected: {selectedCategory.name}</span>
                    </div>
                )}
            </div>

            {selectedCategory && (
                <>
                    <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "var(--text-primary)", margin: "0 0 1rem 0.25rem" }}>
                        Category: {selectedCategory.name}
                    </div>

                    {/* Located in…: only for fixed-location categories (rentals,
                        real estate) — matches the listing's own location. */}
                    {selectedCategory?.hasItemLocation && (
                    <div style={boxStyle}>
                        <div style={boxHeaderStyle}>
                            <label style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                                <input
                                    type="checkbox"
                                    checked={showItemLocation}
                                    onChange={(e) => toggleItemLocation(e.target.checked)}
                                />
                                Located in… <span style={{ fontWeight: 400, color: "var(--text-secondary)" }}>(choose the area where you are looking)</span>
                            </label>
                        </div>
                        {showItemLocation && (
                            <div style={{ marginTop: "0.5rem" }}>
                                <GeoAreaPicker
                                    token={token ?? ""}
                                    context="itemSearcher"
                                    onChange={(chain) => {
                                        const deepest = chain.length ? chain[chain.length - 1] : null;
                                        setItemGeoSearch(deepest
                                            ? { geoSearchType: "geoId", id: Number(deepest.id), geoIdName: deepest.level }
                                            : null);
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    )}

                    {/* Location filter ("near me") — hidden for service categories,
                        which use the Service coverage picker instead, and for
                        fixed-location categories, which use "Located in…". */}
                    {!selectedCategory?.hasServiceArea && !selectedCategory?.hasItemLocation && (
                    <div style={boxStyle}>
                        <div style={boxHeaderStyle}>
                            <label style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                                <input
                                    type="checkbox"
                                    checked={showLocation}
                                    onChange={(e) => toggleLocation(e.target.checked)}
                                />
                                Location Filter <span style={{ fontWeight: 400, color: "var(--text-secondary)" }}>(optional)</span>
                            </label>
                        </div>
                        {showLocation && (
                            <div style={{ marginTop: "0.5rem" }}>
                                <GeoSearchSelector token={token!} onChange={setGeoSearch} />
                            </div>
                        )}
                    </div>
                    )}

                    {/* Service coverage: only for service categories. */}
                    {selectedCategory?.hasServiceArea && (
                    <div style={boxStyle}>
                        <div style={boxHeaderStyle}>
                            <label style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                                <input
                                    type="checkbox"
                                    checked={showService}
                                    onChange={(e) => toggleService(e.target.checked)}
                                />
                                Service needed in… <span style={{ fontWeight: 400, color: "var(--text-secondary)" }}>(choose the area where you need the service)</span>
                            </label>
                        </div>
                        {showService && (
                            <div style={{ marginTop: "0.5rem" }}>
                                <GeoAreaPicker
                                    token={token ?? ""}
                                    context="searcher"
                                    onChange={(chain) => setCoverageChain(chain.length ? chainToKeys(chain) : null)}
                                />
                            </div>
                        )}
                    </div>
                    )}

                    <div style={boxStyle}>
                        <div style={boxHeaderStyle}>
                            Parameters
                        </div>
                        <DynamicForm
                            key={selectedCategory.id} // fresh form state per category (params share names across categories)
                            parameters={params}
                            categoryId={selectedCategory.id}
                            units={units}
                            onSubmit={handleFormSubmit}
                            formId="listing-search-form"
                            hideSubmit
                        />
                    </div>

                    {/* Single action bar: submits Parameters + Location together. */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem", margin: "1.25rem 0" }}>
                        <button
                            type="submit"
                            form="listing-search-form"
                            className="pf-submit"
                            disabled={loading}
                            style={{ minWidth: "12rem" }}
                        >
                            {loading ? "Searching…" : "Search"}
                        </button>
                        {error && <p className="status-error" style={{ margin: 0 }}>{error}</p>}
                    </div>
                </>
            )}

            {searchResults.length > 0 && selectedCategory && (
                <div style={boxStyle}>
                    <div style={boxHeaderStyle}>Search Results</div>

                    <div className="results-table-wrapper">
                        <table>
                            <thead>
                            <tr>
                                <th>No</th>
                                <th>Name</th>
                                <th>Description</th>
                                <th>Coverage</th>
                                <th>Contact</th>
                                <th>URL</th>
                                {params.map((param, index) => (
                                    <th key={index}>{param.name}</th>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {searchResults.map((item, index) => (
                                <tr
                                    key={item.id}
                                    onClick={() => setDetailItem(item)}
                                    style={{ cursor: "pointer" }}
                                    title="Click for details"
                                >
                                    <td>{index + 1}</td>
                                    <td>{item.name}</td>
                                    <td className="results-text-cell">
                                        {item.description && (
                                            <div className="results-cell-scroll">{item.description}</div>
                                        )}
                                    </td>
                                    <td className="results-text-cell">
                                        {coverageSummary(item.coverage) && (
                                            <div className="results-cell-scroll">{coverageSummary(item.coverage)}</div>
                                        )}
                                        {item.location?.label && (
                                            <div className="results-cell-scroll">📍 {item.location.label}</div>
                                        )}
                                    </td>
                                    <td>
                                        <ContactActions item={item} token={token ?? ""} compact />
                                    </td>
                                    <td>
                                        {item.url ? (
                                            <a
                                                href={inventoryHref(item.url)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title={item.url}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {item.name?.trim() || "View"}
                                            </a>
                                        ) : null}
                                    </td>
                                    {params.map((param, idx) => {
                                        const found = item.parameters?.find((p: any) => p.name === param.name);
                                        const raw = found?.value ?? "";
                                        const paramValue = Array.isArray(raw)
                                            ? raw.map((v: any) => (typeof v === "object" && v !== null ? v.name : v)).join(", ")
                                            : typeof raw === "object" && raw !== null
                                            ? raw.name ?? ""
                                            : raw;
                                        const isNumeric = param.dataType === ParameterDataType.INTEGER || param.dataType === ParameterDataType.DOUBLE;
                                        // Prefer the item's own stated unit; fall back to category unit for integers.
                                        const cellUnitId = isNumeric ? (found?.units ?? param.unitId) : undefined;
                                        const unitLabel = cellUnitId
                                            ? (units.find(u => u.id === cellUnitId)?.name ?? cellUnitId)
                                            : null;
                                        return (
                                            <td key={idx}>
                                                {paramValue}
                                                {unitLabel && paramValue !== "" && (
                                                    <span style={{ marginLeft: "0.3rem", fontSize: "0.82rem", color: "var(--text-secondary)" }}>{unitLabel}</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {detailItem && (
                <InventoryDetailModal
                    item={detailItem}
                    params={params}
                    units={units}
                    token={token ?? ""}
                    onClose={() => setDetailItem(null)}
                />
            )}
        </div>
    );
};

export default SearchComponent;